<template>
  <main class="trial-crm-page">
    <header class="page-head">
      <div class="title-block">
        <p class="eyebrow">TRIAL PIPELINE</p>
        <div class="title-row">
          <h1>체험 고객 관리</h1>
          <span v-if="loaded" class="total"
            >{{ total.toLocaleString("ko-KR") }}건</span
          >
        </div>
        <p>
          체험 이후의 반응과 다음 연락을 기록해 등록 전환을 놓치지 않습니다.
          같은 번호의 재방문은 한 건으로 묶어 한 줄로 보여줍니다.
        </p>
      </div>
      <button type="button" class="create-btn" @click="openInquiryDialog">
        <span aria-hidden="true">＋</span> 문의 등록
      </button>
    </header>

    <nav class="queue-tabs" aria-label="체험 고객 업무 목록">
      <button
        v-for="queue in queues"
        :key="queue.value"
        type="button"
        :class="{ active: state.queue === queue.value }"
        :aria-pressed="state.queue === queue.value"
        :disabled="loading"
        @click="setQueue(queue.value)"
      >
        {{ queue.label }}
      </button>
    </nav>

    <section class="filters" aria-label="체험 고객 검색 조건">
      <label class="search-field">
        <span aria-hidden="true">⌕</span>
        <input
          v-model="searchInput"
          type="search"
          placeholder="이름 또는 전화번호 검색"
          :disabled="loading"
          @keyup.enter="applySearch"
        />
        <button
          v-if="searchInput || state.search"
          type="button"
          @click="clearSearch"
        >
          지우기
        </button>
      </label>
      <label>
        <span class="sr-only">담당 코치</span>
        <select
          :value="state.assignee"
          :disabled="loading"
          @change="setFilter('assignee', $event.target.value)"
        >
          <option value="">담당 전체</option>
          <option value="unassigned">담당 미지정</option>
          <option v-for="coach in coaches" :key="coach.id" :value="coach.id">
            {{ coach.name }}
          </option>
        </select>
      </label>
      <div class="date-range">
        <label>
          <span>시작</span>
          <input
            type="date"
            :value="state.dateFrom"
            :disabled="loading"
            @change="setFilter('dateFrom', $event.target.value)"
          />
        </label>
        <i>–</i>
        <label>
          <span>종료</span>
          <input
            type="date"
            :value="state.dateTo"
            :disabled="loading"
            @change="setFilter('dateTo', $event.target.value)"
          />
        </label>
      </div>
    </section>

    <section class="list-panel" :class="{ busy: loading && customers.length }">
      <div class="list-head" aria-hidden="true">
        <span>이름·전화·방문</span>
        <span>방문 일정</span>
        <span>현재 상태</span>
        <span>담당</span>
        <span>다음 행동</span>
        <span></span>
      </div>

      <div v-if="loading && !customers.length" class="state-card">
        <span class="spinner" aria-hidden="true"></span>
        <strong>체험 고객을 불러오는 중입니다.</strong>
      </div>
      <div v-else-if="loadError" class="state-card error" role="alert">
        <strong>목록을 불러오지 못했습니다.</strong>
        <span>잠시 후 다시 시도해 주세요.</span>
        <button type="button" @click="loadCustomers">다시 시도</button>
      </div>
      <div v-else-if="!customers.length" class="state-card">
        <strong>{{ emptyTitle }}</strong>
        <span>{{ emptyDescription }}</span>
      </div>
      <div v-else class="rows">
        <TrialCustomerRow
          v-for="customer in customers"
          :key="customer.request_id"
          :customer="customer"
          @open="openCustomer"
        />
      </div>

      <footer v-if="loaded && (customers.length || total)" class="pagination">
        <span>전체 {{ total.toLocaleString("ko-KR") }}건</span>
        <div>
          <button
            type="button"
            aria-label="이전 페이지"
            :disabled="loading || state.page <= 1"
            @click="setPage(state.page - 1)"
          >
            ‹
          </button>
          <strong>{{ state.page }} / {{ totalPages }}</strong>
          <button
            type="button"
            aria-label="다음 페이지"
            :disabled="loading || (!hasMore && state.page >= totalPages)"
            @click="setPage(state.page + 1)"
          >
            ›
          </button>
        </div>
      </footer>
    </section>

    <TrialCustomerDetail
      ref="detail"
      :open="detailOpen"
      :customer="selectedCustomer"
      :coaches="coaches"
      :saving="savingProfile"
      :contact-submitting="savingContact"
      :legacy-saving="savingLegacy"
      :error="detailError"
      :can-manage-booking="isAdmin"
      :can-edit-legacy="isAdmin"
      @close="closeDetail"
      @save="saveProfile"
      @add-contact="addContact"
      @open-member="openMember"
      @legacy-action="handleLegacyAction"
      @reload="reloadSelected"
      @dirty-change="detailDirty = $event"
    />

    <TrialInquiryDialog
      :open="inquiryOpen"
      :coaches="coaches"
      :saving="creatingInquiry"
      :error="inquiryError"
      @close="closeInquiryDialog"
      @create="createInquiry"
      @dirty-change="inquiryDirty = $event"
    />
  </main>
</template>

<script>
import TrialCustomerDetail from "@/components/trials/TrialCustomerDetail.vue";
import TrialCustomerRow from "@/components/trials/TrialCustomerRow.vue";
import TrialInquiryDialog from "@/components/trials/TrialInquiryDialog.vue";
import { getUser } from "@/lib/session";
import {
  TRIAL_QUEUES,
  appendTrialContact,
  createTrialInquiry,
  fetchTrialCoaches,
  fetchTrialCustomer,
  fetchTrialCustomerByBooking,
  fetchTrialCustomers,
  updateLegacyTrialRequest,
  updateTrialCustomer,
} from "@/lib/trialCRM";

const PAGE_SIZE = 20;
const DEFAULT_STATE = Object.freeze({
  queue: "today",
  search: "",
  assignee: "",
  dateFrom: "",
  dateTo: "",
  page: 1,
});

function queryValue(value) {
  const first = Array.isArray(value) ? value[0] : value;
  return first == null ? "" : String(first);
}

export function parseTrialCRMQuery(query = {}) {
  const queue = queryValue(query.queue);
  const page = queryValue(query.page);
  return {
    queue: TRIAL_QUEUES.some((item) => item.value === queue)
      ? queue
      : DEFAULT_STATE.queue,
    search: queryValue(query.q).trim(),
    assignee: queryValue(query.assignee),
    dateFrom: /^\d{4}-\d{2}-\d{2}$/.test(queryValue(query.date_from))
      ? queryValue(query.date_from)
      : "",
    dateTo: /^\d{4}-\d{2}-\d{2}$/.test(queryValue(query.date_to))
      ? queryValue(query.date_to)
      : "",
    page: /^[1-9]\d{0,4}$/.test(page) ? Number(page) : DEFAULT_STATE.page,
  };
}

export function buildTrialCRMQuery(state, currentQuery = {}) {
  const query = {};
  if (state.queue !== DEFAULT_STATE.queue) query.queue = state.queue;
  if (state.search) query.q = state.search;
  if (state.assignee) query.assignee = state.assignee;
  if (state.dateFrom) query.date_from = state.dateFrom;
  if (state.dateTo) query.date_to = state.dateTo;
  if (state.page > 1) query.page = String(state.page);
  const requestId = queryValue(currentQuery.request_id);
  const bookingId = queryValue(currentQuery.booking_id);
  if (requestId) query.request_id = requestId;
  else if (bookingId) query.booking_id = bookingId;
  return query;
}

export default {
  name: "FreeTrialRequestsView",
  components: { TrialCustomerDetail, TrialCustomerRow, TrialInquiryDialog },
  data() {
    return {
      queues: TRIAL_QUEUES,
      state: { ...DEFAULT_STATE },
      searchInput: "",
      customers: [],
      coaches: [],
      total: 0,
      hasMore: false,
      loaded: false,
      loading: false,
      loadError: false,
      listSeq: 0,
      detailOpen: false,
      selectedCustomer: null,
      detailError: "",
      detailSeq: 0,
      detailDirty: false,
      savingProfile: false,
      savingContact: false,
      savingLegacy: false,
      inquiryOpen: false,
      creatingInquiry: false,
      inquiryError: "",
      inquiryDirty: false,
      allowDraftNavigation: false,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / PAGE_SIZE));
    },
    isAdmin() {
      return getUser()?.role === "admin";
    },
    emptyTitle() {
      if (this.state.queue === "today") return "오늘 예정된 체험이 없습니다.";
      if (this.state.queue === "needs_record")
        return "체험 기록을 기다리는 고객이 없습니다.";
      if (this.state.queue === "follow_up")
        return "오늘 연락할 고객이 없습니다.";
      if (this.state.queue === "overdue") return "기한이 지난 연락이 없습니다.";
      return "조건에 맞는 체험 고객이 없습니다.";
    },
    emptyDescription() {
      return this.state.search ||
        this.state.assignee ||
        this.state.dateFrom ||
        this.state.dateTo
        ? "검색 조건을 바꾸거나 지워 보세요."
        : "새 문의가 들어오면 이곳에서 관리할 수 있습니다.";
    },
  },
  watch: {
    "$route.query": {
      deep: true,
      handler() {
        this.syncFromRoute();
      },
    },
  },
  created() {
    this.syncFromRoute();
    this.loadCoaches();
  },
  beforeUnmount() {
    this.listSeq += 1;
    this.detailSeq += 1;
  },
  beforeRouteUpdate(to, from, next) {
    if (
      this.allowDraftNavigation ||
      this.routeDetailKey(to) === this.routeDetailKey(from) ||
      (!this.detailDirty && !this.inquiryDirty) ||
      window.confirm("저장하지 않은 내용이 있습니다. 이동할까요?")
    ) {
      next();
      return;
    }
    next(false);
  },
  beforeRouteLeave(to, from, next) {
    if (
      this.allowDraftNavigation ||
      (!this.detailDirty && !this.inquiryDirty) ||
      window.confirm("저장하지 않은 내용이 있습니다. 이동할까요?")
    ) {
      next();
      return;
    }
    next(false);
  },
  methods: {
    routeDetailKey(route) {
      return (
        queryValue(route.query.request_id) ||
        `booking:${queryValue(route.query.booking_id)}`
      );
    },
    // 저장 중 표시를 모두 끈다. 상세를 다시 열거나 주소가 바뀌면 앞 요청의 저장 중
    // 상태를 끌고 가지 않는다(응답이 늦게 와도 각 요청의 finally가 다시 끈다)
    resetSavingFlags() {
      this.savingProfile = false;
      this.savingContact = false;
      this.savingLegacy = false;
    },
    syncFromRoute() {
      this.resetSavingFlags();
      const next = parseTrialCRMQuery(this.$route.query);
      const listChanged = JSON.stringify(next) !== JSON.stringify(this.state);
      this.state = next;
      this.searchInput = next.search;
      if (listChanged || !this.loaded) this.loadCustomers();

      const requestId = queryValue(this.$route.query.request_id);
      const bookingId = queryValue(this.$route.query.booking_id);
      if (requestId) {
        if (this.selectedCustomer?.request_id !== requestId)
          this.loadDetail({ requestId });
      } else if (bookingId) {
        // 예약은 에피소드 안 어느 방문의 것일 수도 있다(드랍인 예약으로 들어온 deep-link).
        // 대표 행 방문만 보면 이미 열린 상세를 조건이 바뀔 때마다 다시 불러온다
        const opened = this.selectedCustomer?.visits || [];
        if (!opened.some((visit) => visit.booking_id === bookingId))
          this.loadDetail({ bookingId });
      } else if (this.detailOpen) {
        this.detailSeq += 1;
        this.detailOpen = false;
        this.detailDirty = false;
        this.selectedCustomer = null;
        this.detailError = "";
      }
    },
    updateRoute(patch, { replace = false } = {}) {
      const state = { ...this.state, ...patch };
      const query = buildTrialCRMQuery(state, this.$route.query);
      const method = replace ? "replace" : "push";
      return this.$router[method]({ path: this.$route.path, query }).catch(
        () => {}
      );
    },
    async loadCustomers() {
      const seq = ++this.listSeq;
      this.loading = true;
      this.loadError = false;
      try {
        const response = await fetchTrialCustomers({
          queue: this.state.queue,
          search: this.state.search,
          assignee: this.state.assignee,
          dateFrom: this.state.dateFrom,
          dateTo: this.state.dateTo,
          limit: PAGE_SIZE,
          offset: (this.state.page - 1) * PAGE_SIZE,
        });
        if (seq !== this.listSeq) return;
        if (
          !response.customers.length &&
          this.state.page > 1 &&
          response.total > 0
        ) {
          this.updateRoute(
            { page: Math.max(1, Math.ceil(response.total / PAGE_SIZE)) },
            { replace: true }
          );
          return;
        }
        this.customers = response.customers;
        this.total = response.total;
        this.hasMore = response.hasMore;
        this.loaded = true;
      } catch {
        if (seq !== this.listSeq) return;
        this.loadError = true;
        this.loaded = true;
      } finally {
        if (seq === this.listSeq) this.loading = false;
      }
    },
    async loadCoaches() {
      try {
        this.coaches = await fetchTrialCoaches();
      } catch {
        this.coaches = [];
      }
    },
    setQueue(queue) {
      if (queue !== this.state.queue) this.updateRoute({ queue, page: 1 });
    },
    setFilter(key, value) {
      if (value !== this.state[key])
        this.updateRoute({ [key]: value, page: 1 });
    },
    setPage(page) {
      if (page >= 1 && page !== this.state.page) this.updateRoute({ page });
    },
    applySearch() {
      this.updateRoute({ search: this.searchInput.trim(), page: 1 });
    },
    clearSearch() {
      this.searchInput = "";
      this.updateRoute({ search: "", page: 1 });
    },
    openCustomer(customer) {
      this.$router
        .push({
          path: this.$route.path,
          query: {
            ...buildTrialCRMQuery(this.state),
            request_id: customer.request_id,
          },
        })
        .catch(() => {});
    },
    async loadDetail({ requestId = "", bookingId = "" }) {
      const seq = ++this.detailSeq;
      this.resetSavingFlags();
      this.detailOpen = true;
      this.selectedCustomer = null;
      this.detailError = "";
      try {
        const detail = requestId
          ? await fetchTrialCustomer(requestId)
          : await fetchTrialCustomerByBooking(bookingId);
        if (seq !== this.detailSeq) return;
        if (!detail) {
          this.detailError = "연결된 체험 고객을 찾지 못했습니다.";
          return;
        }
        this.selectedCustomer = detail;
      } catch {
        if (seq !== this.detailSeq) return;
        this.detailError =
          "상세 정보를 불러오지 못했습니다. 다시 시도해 주세요.";
      }
    },
    closeDetail() {
      const query = buildTrialCRMQuery(this.state);
      this.detailDirty = false;
      this.allowDraftNavigation = true;
      this.$router
        .push({ path: this.$route.path, query })
        .catch(() => {})
        .finally(() => {
          this.allowDraftNavigation = false;
        });
    },
    async reloadSelected() {
      const requestId =
        this.selectedCustomer?.request_id ||
        queryValue(this.$route.query.request_id);
      if (requestId) await this.loadDetail({ requestId });
    },
    conflictMessage(error) {
      return error?.response?.status === 409
        ? "다른 코치가 먼저 수정했습니다. 최신 내용을 다시 불러온 뒤 변경 사항을 확인해 주세요."
        : "저장하지 못했습니다. 잠시 후 다시 시도해 주세요.";
    },
    async saveProfile(payload) {
      if (this.savingProfile || !this.selectedCustomer) return;
      const requestId = this.selectedCustomer.request_id;
      const seq = this.detailSeq;
      this.savingProfile = true;
      this.detailError = "";
      try {
        const detail = await updateTrialCustomer(requestId, payload);
        if (
          seq !== this.detailSeq ||
          this.selectedCustomer?.request_id !== requestId
        )
          return;
        this.selectedCustomer = detail;
        await this.loadCustomers();
      } catch (error) {
        if (seq !== this.detailSeq) return;
        this.detailError = this.conflictMessage(error);
      } finally {
        // 응답이 늦게 와도 저장 중 표시는 반드시 푼다. seq로 막으면 저장 중 뒤로가기 한
        // 번에 플래그가 영구히 켜져 이후 모든 상세의 입력과 닫기가 잠겼다. 화면 상태
        // 반영은 위 try/catch 본문에서 이미 seq를 본다
        this.savingProfile = false;
      }
    },
    async addContact(payload) {
      if (this.savingContact || !this.selectedCustomer) return;
      const requestId = this.selectedCustomer.request_id;
      const seq = this.detailSeq;
      this.savingContact = true;
      this.detailError = "";
      try {
        await appendTrialContact(requestId, payload);
        if (seq !== this.detailSeq) return;
        const detail = await fetchTrialCustomer(requestId);
        if (
          seq !== this.detailSeq ||
          this.selectedCustomer?.request_id !== requestId
        )
          return;
        this.$refs.detail?.acceptContactSaved();
        this.selectedCustomer = detail;
        await this.loadCustomers();
      } catch (error) {
        if (seq !== this.detailSeq) return;
        this.detailError = this.conflictMessage(error);
      } finally {
        this.savingContact = false;
      }
    },
    openInquiryDialog() {
      this.inquiryError = "";
      this.inquiryDirty = false;
      this.inquiryOpen = true;
    },
    closeInquiryDialog() {
      this.inquiryOpen = false;
      this.inquiryDirty = false;
      this.inquiryError = "";
    },
    async createInquiry(payload) {
      if (this.creatingInquiry) return;
      this.creatingInquiry = true;
      this.inquiryError = "";
      try {
        const detail = await createTrialInquiry(payload);
        this.inquiryOpen = false;
        this.inquiryDirty = false;
        await this.loadCustomers();
        this.$router
          .push({
            path: this.$route.path,
            query: {
              ...buildTrialCRMQuery(this.state),
              request_id: detail.request_id,
            },
          })
          .catch(() => {});
      } catch (error) {
        this.inquiryError =
          error?.response?.status === 409
            ? "같은 전화번호로 진행 중인 문의가 이미 있습니다. 기존 고객을 검색해 주세요."
            : "문의를 등록하지 못했습니다. 입력 내용을 확인해 주세요.";
      } finally {
        this.creatingInquiry = false;
      }
    },
    openMember(memberId) {
      if (!this.isAdmin || !memberId) return;
      this.$router.push({
        path: `/profile/${encodeURIComponent(memberId)}`,
        query: { from: this.$route.fullPath },
      });
    },
    async handleLegacyAction(action) {
      if (!this.isAdmin || !this.selectedCustomer) return;
      if (action.type === "book") {
        this.allowDraftNavigation = true;
        this.$router
          .push({
            path: "/booking",
            query: { trial_request_id: action.requestId },
          })
          .finally(() => {
            this.allowDraftNavigation = false;
          });
        return;
      }
      if (action.type !== "update" || this.savingLegacy) return;
      const requestId = this.selectedCustomer.request_id;
      const seq = this.detailSeq;
      this.savingLegacy = true;
      this.detailError = "";
      try {
        await updateLegacyTrialRequest(action.payload);
        if (seq !== this.detailSeq) return;
        this.$refs.detail?.acceptLegacySaved();
        this.selectedCustomer = await fetchTrialCustomer(requestId);
        await this.loadCustomers();
      } catch {
        if (seq !== this.detailSeq) return;
        this.detailError = "기존 신청 정보를 저장하지 못했습니다.";
      } finally {
        this.savingLegacy = false;
      }
    },
  },
};
</script>

<style scoped>
.trial-crm-page {
  width: 100%;
  min-width: 0;
  padding: 26px 28px 40px;
  color: #fff;
}
.page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #78ff47;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
}
.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.035em;
}
.total {
  padding: 3px 8px;
  border-radius: 999px;
  background: #242829;
  color: #aeb4b0;
  font-size: 12px;
}
.title-block > p:last-child {
  margin: 7px 0 0;
  color: #858c88;
  font-size: 13px;
}
.create-btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 17px;
  border: 1px solid #78ff47;
  border-radius: 9px;
  background: #78ff47;
  color: #0b1507;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}
.create-btn span {
  font-size: 18px;
  line-height: 1;
}
.queue-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
  overflow-x: auto;
  scrollbar-width: none;
}
.queue-tabs button {
  min-height: 39px;
  flex: none;
  padding: 0 14px;
  border: 1px solid #313536;
  border-radius: 9px;
  background: #17191a;
  color: #9da39f;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.queue-tabs button.active {
  border-color: #78ff47;
  background: rgba(120, 255, 71, 0.1);
  color: #aaff8a;
}
.filters {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(150px, 210px) auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #2b2f30;
  border-radius: 12px;
  background: #141617;
}
.search-field {
  position: relative;
}
.search-field > span {
  position: absolute;
  top: 50%;
  left: 13px;
  z-index: 1;
  color: #747b77;
  font-size: 21px;
  transform: translateY(-54%);
}
.search-field input {
  padding-left: 38px;
  padding-right: 58px;
}
.search-field button {
  position: absolute;
  top: 50%;
  right: 7px;
  border: 0;
  background: transparent;
  color: #858c88;
  font-size: 11px;
  transform: translateY(-50%);
  cursor: pointer;
}
.filters input,
.filters select {
  width: 100%;
  height: 42px;
  border: 1px solid #343839;
  border-radius: 8px;
  outline: none;
  background: #0f1112;
  color: #eef1ef;
  font: inherit;
  font-size: 12px;
}
.filters input {
  padding: 0 11px;
}
.filters select {
  padding: 0 32px 0 11px;
}
.filters input:focus,
.filters select:focus {
  border-color: #78ff47;
}
.date-range {
  display: flex;
  align-items: center;
  gap: 7px;
}
.date-range label {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #777e7a;
  font-size: 10px;
}
.date-range input {
  width: 138px;
}
.date-range i {
  color: #656b68;
  font-style: normal;
}
.list-panel {
  overflow: hidden;
  border: 1px solid #2c3031;
  border-radius: 14px;
  background: #111314;
}
.list-panel.busy {
  opacity: 0.66;
  pointer-events: none;
}
.list-head {
  display: grid;
  grid-template-columns:
    minmax(160px, 1.25fr) minmax(150px, 1.15fr) minmax(190px, 1.5fr)
    minmax(100px, 0.7fr) minmax(125px, 0.85fr) 20px;
  gap: 14px;
  padding: 11px 18px;
  border-bottom: 1px solid #292d2e;
  color: #747b77;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
}
.rows {
  display: grid;
  gap: 8px;
  padding: 10px;
}
.state-card {
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 30px;
  color: #818884;
  text-align: center;
}
.state-card strong {
  color: #d7dbd8;
  font-size: 15px;
}
.state-card span {
  font-size: 12px;
}
.state-card button {
  margin-top: 8px;
  padding: 8px 13px;
  border: 1px solid #3d4243;
  border-radius: 7px;
  background: #1c1f20;
  color: #fff;
  cursor: pointer;
}
.state-card.error strong {
  color: #ff9da2;
}
.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #34393a;
  border-top-color: #78ff47;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.pagination {
  min-height: 57px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  border-top: 1px solid #292d2e;
  color: #777e7a;
  font-size: 11px;
}
.pagination div {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pagination strong {
  min-width: 70px;
  color: #bdc2bf;
  font-size: 12px;
  text-align: center;
}
.pagination button {
  width: 35px;
  height: 35px;
  border: 1px solid #363a3b;
  border-radius: 8px;
  background: #1a1d1e;
  color: #fff;
  font-size: 19px;
  cursor: pointer;
}
button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1100px) {
  .filters {
    grid-template-columns: 1fr 180px;
  }
  .date-range {
    grid-column: 1 / -1;
  }
  .list-head {
    grid-template-columns: 1.2fr 1fr auto 18px;
  }
  .list-head span:nth-child(4),
  .list-head span:nth-child(5) {
    display: none;
  }
}
@media (max-width: 700px) {
  .trial-crm-page {
    padding: 18px 12px 30px;
  }
  .page-head {
    align-items: flex-start;
  }
  h1 {
    font-size: 23px;
  }
  .title-block > p:last-child {
    display: none;
  }
  .create-btn {
    min-height: 40px;
    padding: 0 12px;
  }
  .filters {
    grid-template-columns: 1fr;
  }
  .date-range {
    grid-column: auto;
    display: grid;
    grid-template-columns: 1fr 8px 1fr;
  }
  .date-range label {
    display: block;
  }
  .date-range label span {
    display: none;
  }
  .date-range input {
    width: 100%;
  }
  .list-head {
    display: none;
  }
}
</style>
